/**
 * pages/signup/steps/Verifying.tsx — transitional "we're checking" screen.
 *
 * Reached when submit returned `verifying` (server round-tripping to a
 * real KYC vendor that decides asynchronously) or when a client
 * ended up here via `unverified` (server-side bug case).
 *
 * We poll GET /v1/identity every 2 seconds up to 60s. If the verdict
 * settles, we route based on the new status. If we time out on
 * `verifying`, we route to /signup/pending — the "we'll email you"
 * copy is the right long-tail landing.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getIdentity, type Identity } from "../../../api/cip";
import { clearProgress } from "../signup-store";
import { SignupShell } from "../SignupShell";
import styles from "./Verifying.module.css";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000;

export function SignupVerifying() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Identity["status"] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const start = Date.now();

    const tick = async () => {
      if (cancelled) return;
      try {
        const id = await getIdentity();
        if (cancelled) return;
        setStatus(id.status);

        // Terminal or actionable → route.
        if (id.status === "verified") {
          clearProgress();
          navigate("/");
          return;
        }
        if (id.status === "pending") {
          navigate("/signup/pending");
          return;
        }
        if (id.status === "action_needed") {
          navigate("/signup/documents");
          return;
        }
        if (id.status === "rejected") {
          navigate("/signup/rejected");
          return;
        }
        if (id.status === "blocked") {
          navigate("/signup/blocked");
          return;
        }
        // Still verifying / unverified — try again or time out.
        if (Date.now() - start > POLL_TIMEOUT_MS) {
          // Give up on real-time; land on pending which reads
          // "we'll come back to you". Ledger of the submission
          // survives; the client just isn't waiting anymore.
          navigate("/signup/pending");
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch {
        // Network transient — try again. If persistent, we time out
        // to pending after 60s.
        if (Date.now() - start > POLL_TIMEOUT_MS) {
          navigate("/signup/pending");
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <SignupShell
      title="Verifying your identity"
      subtitle="This usually takes a few seconds. Don't refresh — we'll take you to the next step automatically."
    >
      <div className={styles.spinnerWrap}>
        <div className={styles.spinner} aria-label="Verifying" />
        {status && (
          <p className={styles.statusLine}>Current status: {status}</p>
        )}
      </div>
    </SignupShell>
  );
}
